/**
 * Unified icon component covering every glyph used across the mockups.
 *
 * Sourced from `docs/themes/because/Brand Book.html` (+ Login.html for
 * mail/key, TaskList.html for chev/arrow-u). Each glyph uses
 * `currentColor` for stroke/fill so the active theme's --ink, --muted,
 * etc. flow through naturally; size scales with font-size or explicit
 * width/height.
 *
 * Usage:
 *   <Icon name="search" />
 *   <Icon name="bell" size={20} />
 *   <Icon name="plus" style={{ width: 14, height: 14 }} />
 */

import type { ReactNode, SVGAttributes } from "react";

export type IconName =
  | "arrow"
  | "arrow-d"
  | "arrow-u"
  | "bell"
  | "board"
  | "burger"
  | "cal"
  | "chat"
  | "check"
  | "chev"
  | "clip"
  | "clock"
  | "close"
  | "filter"
  | "flag"
  | "folder"
  | "grid"
  | "home"
  | "key"
  | "mail"
  | "moon"
  | "pencil"
  | "pin"
  | "plus"
  | "search"
  | "settings"
  | "star"
  | "sun"
  | "user"
  | "users";

interface IconProps extends Omit<SVGAttributes<SVGSVGElement>, "name"> {
  name: IconName;
  size?: number;
}

// Paths extracted verbatim from the Because brandbook's <symbol> sprites
// so the rendered glyphs match the mockups pixel-for-pixel.
const PATHS: Record<IconName, ReactNode> = {
  arrow: (
    <path
      d="M4 10h12M11 5l5 5-5 5"
      stroke="currentColor"
      strokeWidth="1.6"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  "arrow-d": (
    <path
      d="M10 5v10M6 11l4 4 4-4"
      stroke="currentColor"
      strokeWidth="1.6"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  "arrow-u": (
    <path
      d="M10 15V5M6 9l4-4 4 4"
      stroke="currentColor"
      strokeWidth="1.6"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  bell: (
    <>
      <path
        d="M6 8a4 4 0 018 0c0 4 1.5 5 1.5 5h-11S6 12 6 8z"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        strokeLinejoin="round"
      />
      <path
        d="M8.5 16a1.5 1.5 0 003 0"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
    </>
  ),
  board: (
    <>
      <rect
        x="3"
        y="3.5"
        width="5"
        height="13"
        rx="1.4"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      <rect
        x="12"
        y="3.5"
        width="5"
        height="8"
        rx="1.4"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
    </>
  ),
  burger: (
    <path
      d="M3.5 6h13M3.5 10h13M3.5 14h13"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  ),
  cal: (
    <>
      <rect
        x="3.5"
        y="4.5"
        width="13"
        height="12"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M3.5 8h13M7 3v3M13 3v3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </>
  ),
  chat: (
    <path
      d="M4 5.5A1.5 1.5 0 015.5 4h9A1.5 1.5 0 0116 5.5v6A1.5 1.5 0 0114.5 13H8l-3.5 3v-3a1.5 1.5 0 01-.5-1V5.5z"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      strokeLinejoin="round"
    />
  ),
  check: (
    <path
      d="M4 10.5l4 4 8-9"
      stroke="currentColor"
      strokeWidth="1.7"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  chev: (
    <path
      d="M7 5l5 5-5 5"
      stroke="currentColor"
      strokeWidth="1.6"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  clip: (
    <path
      d="M15 9l-5.5 5.5a3 3 0 01-4.2-4.2L11 4.5a2 2 0 012.8 2.8l-6 6a1 1 0 01-1.4-1.4l5.3-5.3"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  clock: (
    <>
      <circle
        cx="10"
        cy="10"
        r="6.5"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M10 6.5V10l2.5 1.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
    </>
  ),
  close: (
    <path
      d="M5 5l10 10M15 5L5 15"
      stroke="currentColor"
      strokeWidth="1.7"
      fill="none"
      strokeLinecap="round"
    />
  ),
  filter: (
    <path
      d="M4 5h12l-4.5 5.5V15l-3 1.5v-6L4 5z"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      strokeLinejoin="round"
    />
  ),
  flag: (
    <path
      d="M5 3v14M5 4h9l-2 3 2 3H5"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      strokeLinejoin="round"
    />
  ),
  folder: (
    <path
      d="M3 6a1.5 1.5 0 011.5-1.5H8L9.5 6H16a1.5 1.5 0 011.5 1.5v7A1.5 1.5 0 0116 16H4.5A1.5 1.5 0 013 14.5V6z"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      strokeLinejoin="round"
    />
  ),
  grid: (
    <>
      <rect
        x="3"
        y="3"
        width="6"
        height="6"
        rx="1.2"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      <rect
        x="11"
        y="3"
        width="6"
        height="6"
        rx="1.2"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      <rect
        x="3"
        y="11"
        width="6"
        height="6"
        rx="1.2"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      <rect
        x="11"
        y="11"
        width="6"
        height="6"
        rx="1.2"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
    </>
  ),
  home: (
    <path
      d="M4 9l6-5 6 5v7a1 1 0 01-1 1h-3v-5H8v5H5a1 1 0 01-1-1V9z"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      strokeLinejoin="round"
    />
  ),
  key: (
    <>
      <circle
        cx="7"
        cy="7"
        r="3.2"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M9.2 9.2L15 15M13 13l1.6-1.6M14.5 14.5L16 13"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
      />
    </>
  ),
  mail: (
    <>
      <rect
        x="3"
        y="5"
        width="14"
        height="10"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M4 6l6 4.5L16 6"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
      />
    </>
  ),
  moon: (
    <path
      d="M15.5 12.5A7 7 0 016.5 4.5 7 7 0 1015.5 12.5z"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      strokeLinejoin="round"
    />
  ),
  pencil: (
    <path
      d="M13.6 3.6l2.8 2.8L7 15.8l-3.6.8.8-3.6 9.4-9.4z"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  pin: (
    <path
      d="M10 3v6M7 9h6l1 3H6l1-3zm3 3v5"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  plus: (
    <path
      d="M10 4v12M4 10h12"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  ),
  search: (
    <>
      <circle
        cx="9"
        cy="9"
        r="5.5"
        stroke="currentColor"
        strokeWidth="1.7"
        fill="none"
      />
      <path
        d="M13.5 13.5L17 17"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </>
  ),
  settings: (
    <>
      <circle
        cx="10"
        cy="10"
        r="2.4"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M10 3v2.2M10 14.8V17M3 10h2.2M14.8 10H17M5 5l1.6 1.6M13.4 13.4L15 15M15 5l-1.6 1.6M6.6 13.4L5 15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </>
  ),
  star: (
    <path
      d="M10 3l2.1 4.4 4.9.6-3.6 3.3 1 4.7L10 13.6 5.6 16l1-4.7L3 8l4.9-.6L10 3z"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      strokeLinejoin="round"
    />
  ),
  sun: (
    <>
      <circle
        cx="10"
        cy="10"
        r="3.2"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M10 2v2.4M10 15.6V18M2 10h2.4M15.6 10H18M4.5 4.5l1.7 1.7M13.8 13.8l1.7 1.7M15.5 4.5l-1.7 1.7M6.2 13.8l-1.7 1.7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </>
  ),
  user: (
    <>
      <circle
        cx="10"
        cy="7"
        r="3"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M4 17c0-3 2.7-5 6-5s6 2 6 5"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
      />
    </>
  ),
  users: (
    <>
      <circle
        cx="7.5"
        cy="7"
        r="2.6"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M3.5 16c0-2.5 1.8-4 4-4s4 1.5 4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M13 5.5a2.4 2.4 0 010 4.5M14 16c0-2-.8-3.3-2-4"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
      />
    </>
  ),
};

export function Icon({ name, size = 16, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      aria-hidden="true"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
